import { DateTime } from 'luxon';
import { AttendanceCheckMonthStatus } from '@diritto/common/activityPoint/constants';
import {
  AttendanceCheckMonthDto,
  AttendanceCheckMonthParticipateDto,
} from '@diritto/common/activityPoint/dtos/attendance-check.dtos';

export enum AttendanceStatus {
  NotAvailEvent = 'NotAvailEvent',
  CanAttend = 'CanAttend',
  Attended = 'Attended',
  Aggregating = 'Aggregating',
  RaffleCompleted = 'RaffleCompleted',
  Past = 'Past',
}

// 출석체크 이벤트 종료 시점
export const ATTENDANCE_CHECK_EVENT_END_YEAR_MONTH = '2026-05';

const isAttendanceCheckEventEnded = (targetMonth: DateTime) =>
  targetMonth >= DateTime.fromFormat(ATTENDANCE_CHECK_EVENT_END_YEAR_MONTH, 'yyyy-MM');

export const isAvailAttend = (targetMonth: DateTime, now: DateTime) =>
  targetMonth.startOf('day').minus({ day: 7 }) <= now; // 7일전에 미리 오픈한다.

export const getDateInfo = (yearMonth: string) => {
  const targetMonth = DateTime.fromFormat(yearMonth, 'yyyy-MM');
  const prevYearMonth = targetMonth.minus({ months: 1 }).toFormat('yyyy-MM');
  const availPrev = targetMonth.minus({ months: 1 }) >= DateTime.fromFormat('2023-01', 'yyyy-MM');
  const nextYearMonth = targetMonth.plus({ months: 1 }).toFormat('yyyy-MM');
  const availNext = isAvailAttend(targetMonth.plus({ months: 1 }), DateTime.now().setZone('Asia/Seoul'));

  return {
    yearMonth,
    targetMonth,
    prevYearMonth,
    availPrev,
    nextYearMonth,
    availNext,
  };
};

export const getAttendanceCheckStatus = ({
  attendanceCheckMonth,
  attendanceCheckMonthParticipate,
}: {
  attendanceCheckMonth: AttendanceCheckMonthDto;
  attendanceCheckMonthParticipate?: AttendanceCheckMonthParticipateDto;
}) => {
  // 출석체크 이벤트를 하지 않는 달이라면 출석 불가
  if (!attendanceCheckMonth) {
    return AttendanceStatus.NotAvailEvent;
  }

  const thisMonth = DateTime.now().setZone('Asia/Seoul');
  const targetMonth = DateTime.fromFormat(attendanceCheckMonth.yearMonth, 'yyyy-MM');
  const isParticipated = !!attendanceCheckMonthParticipate?.isParticipated;

  // 이벤트 종료 시점 이후의 달은 DB에 데이터가 있어도 도전 불가
  if (isAttendanceCheckEventEnded(targetMonth)) {
    return AttendanceStatus.NotAvailEvent;
  }

  // 아직 참여할 수 있는지 확인
  const isThisMonth = thisMonth.month === targetMonth.month;
  const isLastMonth = thisMonth.minus({ months: 1 }).month === targetMonth.month;
  const isFutureMonth = !isThisMonth && thisMonth < targetMonth;
  const availParticipateTargetMonth = isThisMonth || (isFutureMonth && isAvailAttend(targetMonth, thisMonth));

  // 이번 달 참여할 수 있는지
  if (availParticipateTargetMonth && !isParticipated) {
    return AttendanceStatus.CanAttend;
  }

  // 이번 달 참여 했는지
  if (availParticipateTargetMonth && isParticipated) {
    return AttendanceStatus.Attended;
  }

  if (!availParticipateTargetMonth && isFutureMonth) {
    return AttendanceStatus.NotAvailEvent;
  }

  // 지난달 참여 했고 집계중인지
  if (isLastMonth && isParticipated && attendanceCheckMonth.status !== AttendanceCheckMonthStatus.RaffleCompleted) {
    return AttendanceStatus.Aggregating;
  }

  // 지난달 참여 했고 집계 완료 되었는지
  if (isLastMonth && isParticipated && attendanceCheckMonth.status === AttendanceCheckMonthStatus.RaffleCompleted) {
    return AttendanceStatus.RaffleCompleted;
  }

  return AttendanceStatus.Past;
};
